import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Edit, MoreHorizontal, Users, Settings, Archive, CheckCircle, Trash2 } from 'lucide-react';
import type { LeavePolicyWithRules } from '@/services/leave/leavePolicyTypes';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';

interface PolicyListCardProps {
  policy: LeavePolicyWithRules;
  onEdit: (policy: LeavePolicyWithRules) => void;
  onDeactivate: (policyId: string) => void;
  onReactivate: (policyId: string) => void;
  onDelete: (policyId: string) => void;
  onAssignEmployees: (policyId: string) => void;
  onViewBalances: (policyId: string) => void;
}

const accrualMethodLabels = {
  does_not_accrue: 'Does Not Accrue',
  hourly_worked: 'Hourly Worked',
  fixed_accrual: 'Fixed Accrual'
};

export function PolicyListCard({ 
  policy, 
  onEdit, 
  onDeactivate,
  onReactivate,
  onDelete,
  onAssignEmployees, 
  onViewBalances 
}: PolicyListCardProps) {
  const accrualRule = policy.accrual_rule;
  
  const getAccrualDisplay = () => {
    if (!accrualRule) return 'Not configured';
    
    switch (policy.accrual_method) {
      case 'does_not_accrue':
        return 'Manually managed';
      case 'hourly_worked':
        if (accrualRule.hourly_accrual_type === 'per_hour_worked') {
          const rate = accrualRule.accrual_rate || 0;
          const rateType = accrualRule.accrual_rate_type || 'hours';
          return `${rate} ${rateType} per hour worked`;
        } else if (accrualRule.hourly_accrual_type === 'per_threshold_worked') {
          const threshold = accrualRule.threshold_hours_to_work || 0;
          const amount = accrualRule.threshold_leave_amount || 0;
          const unit = accrualRule.threshold_leave_unit || 'hours';
          return `${amount} ${unit} per ${threshold} hours worked`;
        }
        return 'Not configured';
      case 'fixed_accrual':
        const amount = accrualRule.fixed_accrual_amount || 0;
        const unit = accrualRule.fixed_accrual_unit || 'hours';
        const frequency = accrualRule.fixed_accrual_frequency || 'monthly';
        return `${amount} ${unit} per ${frequency}`;
      default:
        return 'Not configured';
    }
  };

  return (
    <Card className="relative group hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg">{policy.name}</CardTitle>
            <CardDescription className="line-clamp-2">
              {policy.description || 'No description provided'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={policy.is_active ? 'default' : 'secondary'}>
              {policy.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(policy)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Policy
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAssignEmployees(policy.id)}>
                  <Users className="mr-2 h-4 w-4" />
                  Assign Employees
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onViewBalances(policy.id)}>
                  <Settings className="mr-2 h-4 w-4" />
                  View Balances
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {policy.is_active ? (
                  <DropdownMenuItem 
                    onClick={() => onDeactivate(policy.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Deactivate Policy
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={() => onReactivate(policy.id)}
                      className="text-green-600 focus:text-green-600"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Reactivate Policy
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(policy.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Policy
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground mb-1">Leave Type</p>
            <p className="font-medium">{getLeaveTypeLabel(policy.leave_type)}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1">Accrual Method</p>
            <p className="font-medium">{accrualMethodLabels[policy.accrual_method]}</p>
          </div>
          <div className="col-span-2">
            <p className="text-muted-foreground mb-1">Accrual Details</p>
            <p className="font-medium">{getAccrualDisplay()}</p>
          </div>
          {policy.assigned_employees_count !== undefined && (
            <div>
              <p className="text-muted-foreground mb-1">Assigned Employees</p>
              <p className="font-medium">{policy.assigned_employees_count} employees</p>
            </div>
          )}
          {policy.approvers && policy.approvers.length > 0 && (
            <div>
              <p className="text-muted-foreground mb-1">Approvers</p>
              <p className="font-medium">{policy.approvers.length} approver(s)</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}